import * as THREE from "three";
import type { Scenery } from "../../prims/scenery";
import type { Kit } from "./kit";

// A water slide tower (#156): a deck on stilts with a stair up to it, one
// enclosed tube spiralling down round the tower and one open chute running
// straight down along +u, both ending at the foot. `h` is the deck height.

export function waterslide(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const h = s.h ?? 8;
  const tube = s.color ?? "#ffd166";
  // Tower: four stilts, a deck with a roof, a stair.
  for (const [x, z] of [
    [-1.2, -1.2],
    [1.2, -1.2],
    [-1.2, 1.2],
    [1.2, 1.2],
  ] as const)
    kit.rod(root, new THREE.Vector3(x, 0, z), new THREE.Vector3(x, h + 2.4, z), 0.12, "#f4f1ea", "paintedMetal");
  kit.box(root, 3, 0.2, 3, "#118ab2", 0, h, 0);
  kit.stripedCone(root, 2.3, 1.2, h + 2.4, 8, ["#118ab2", "#f4f1ea"]);
  // A steep stair up the side.
  const treads = Math.round(h / 0.6);
  for (let k = 0; k < treads; k++) kit.box(root, 0.9, 0.08, 0.3, "#f4f1ea", 2, ((k + 1) * h) / (treads + 1), -2.4 + (k * 3.6) / treads, "paintedMetal");
  kit.rod(root, new THREE.Vector3(2.45, 0, -2.4), new THREE.Vector3(2.45, h, 1.2), 0.06, "#f4f1ea", "paintedMetal");

  // The spiral: two turns round the tower down to the ground, then out.
  const spiral: THREE.Vector3[] = [];
  const turns = 2;
  const steps = 48;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const a = t * turns * Math.PI * 2;
    spiral.push(new THREE.Vector3(Math.cos(a) * 3.8, h - t * (h - 0.8), Math.sin(a) * 3.8));
  }
  spiral.push(new THREE.Vector3(5, 0.6, 0), new THREE.Vector3(7, 0.5, 0));
  const spiralMesh = new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(spiral), 120, 0.55, 10), kit.mat(tube, "plastic", { roughness: 0.4 }));
  spiralMesh.castShadow = true;
  root.add(spiralMesh);

  // The open chute, straight down along -u.
  const chute = new THREE.CatmullRomCurve3([new THREE.Vector3(-1.4, h, 0.8), new THREE.Vector3(-6, h * 0.45, 0.8), new THREE.Vector3(-10, 0.5, 0.8), new THREE.Vector3(-11.5, 0.4, 0.8)]);
  const open = new THREE.Mesh(new THREE.TubeGeometry(chute, 40, 0.6, 10), kit.mat("#06d6a0", "plastic", { roughness: 0.4, side: THREE.DoubleSide }));
  open.castShadow = true;
  root.add(open);
  for (const t of [0.25, 0.5, 0.75]) {
    const p = chute.getPointAt(t);
    kit.rod(root, new THREE.Vector3(p.x, 0, p.z), p.clone().setY(p.y - 0.5), 0.1, "#f4f1ea", "paintedMetal");
  }
  return root;
}
